import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import {
    CaseRequirement,
    CaseRequirementResponse,
    NoteResponse,
    RequirementResponse,
    UWBaseProps,
    UWRequirementActionEvent,
    UWRequirementTableData,
} from '@Underwriting/models';
import { CaseRequirementStatuses, UWRequirementType } from '@Underwriting/types';
import { AccountSettings, FeatureToggle } from '@Enums';

import { UwwUtils } from '../../utils';
import { FeatureManagerService, NotificationService, NotificationSeverity } from '@Services';
import { CaseSummarySettingsService } from '@Services/case/case-summary-settings.service';
import { SubSink } from 'subsink';
import { UWRequirementsDataService, UWWorkbenchService } from '@Underwriting/services';
import { forkJoin } from 'rxjs';
import { AccountSettingsDTO } from '@DTOs';
import { NgIf, NgClass, NgStyle } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { SharedModule } from '@Shared/shared.module';
import { UWRequirementsActionsNotOrderedComponent } from '../uw-requirements-actions-not-ordered';
import { UWRequirementsActionsOrderedComponent } from '../uw-requirements-actions-ordered';
import { UWRequirementsActionsCancelledComponent } from '../uw-requirements-actions-cancelled';
import { UWRequirementsActionsCompletedComponent } from '../uw-requirements-actions-completed';
import { UWRequirementsActionsReceivedComponent } from '../uw-requirements-actions-received';
import { UWRequirementsActionsWaivedComponent } from '../uw-requirements-actions-waived';
import { UWRequirementsActionsFailedComponent } from '../uw-requirements-actions-failed';
import { UWRequirementsActionsPendingComponent } from '../uw-requirements-actions-pending';
import { UWNotesAddForm, UWNotesList } from '@CaseSummary/underwriting/components/notes';
import moment, { Moment } from 'moment';
import { MaskDateDirective } from '@App/directives';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

export enum UWRequirementTableActions {
    ViewDocument = 'view document',
    ViewDocumentIcon = 'view document icon',
    RunIntegration = 'run integration',
    AttachDocument = 'attach document',
    AddAgentCommunications = 'add agent communications',
    OrderSubmitted = 'order submitted',
    FollowUp = 'follow up',
    EditSubtitle = 'edit subtitle',
    Waive = 'waive',
    Completed = 'completed',
    Pending = 'pending',
    Cancelled = 'cancelled',
    Delete = 'delete',
    OpenSection = 'order'
}

export type UWRequirementTableProps = {
    showRequirementLabel: boolean;
    reviewDocumentsEnabled: boolean;
    hideSetPendingMenuOption: boolean;
    hideSetCancelledMenuOption: boolean;
    hideDeleteMenuOption: boolean;
    hideEndStateToEndStateTransition: boolean;
    hideCompleteMenuOption?: boolean;
    autoReviewDocumentSetting?: boolean;
    updatedTableDesignEnabled?: boolean;
}

@Component({
    selector: 'uw-requirements-expand-table',
    templateUrl: './uw-requirements-expand-table.component.html',
    styleUrls: ['./uw-requirements-expand-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
    ],
    imports: [
        NgIf,
        MatProgressSpinner,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatIconButton,
        MatIcon,
        MatTooltip,
        NgClass,
        NgStyle,
        MatMenuTrigger,
        MatMenu,
        SharedModule,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatNoDataRow,
        UWRequirementsActionsNotOrderedComponent,
        UWRequirementsActionsOrderedComponent,
        UWRequirementsActionsReceivedComponent,
        UWRequirementsActionsWaivedComponent,
        UWRequirementsActionsCompletedComponent,
        UWRequirementsActionsFailedComponent,
        UWRequirementsActionsPendingComponent,
        UWRequirementsActionsCancelledComponent,
        UWNotesAddForm,
        UWNotesList,
        MaskDateDirective
    ],
})
export class UWRequirementsExpandTable implements OnInit, OnChanges, OnDestroy {
    @Input() baseProps: UWBaseProps;
    @Input() caseRequirements: CaseRequirementResponse[];
    @Input() requirements: RequirementResponse[];
    @Output() onMenuAction = new EventEmitter<UWRequirementActionEvent>();

    uwRequirementsDataSet = new MatTableDataSource([]);
    noDataMessage = 'No requirements have been added';
    displayedColumns: Array<string> = ['expand', 'name', 'ordered', 'followUp', 'received', 'status', 'actions'];
    expandedElement: CaseRequirement | null;

    featureTogglesLoaded = false;

    notes: NoteResponse[] = [];
    notesLoaded = false;

    minDate: Moment | undefined;
    maxDate: Moment | undefined;

    subs = new SubSink();
    tableProps: UWRequirementTableProps = {
        showRequirementLabel: true,
        reviewDocumentsEnabled: false,
        hideSetPendingMenuOption: false,
        hideSetCancelledMenuOption: false,
        hideDeleteMenuOption: false,
        hideEndStateToEndStateTransition: false,
        updatedTableDesignEnabled: false
    };

    constructor(
        private readonly featureManagerService: FeatureManagerService,
        private readonly uwRequirementsDataService: UWRequirementsDataService,
        private readonly caseSummarySettingsService: CaseSummarySettingsService,
        private readonly uwWorkbenchService: UWWorkbenchService,
        private readonly notificationService: NotificationService
    ) { }

    //#region Utilities

    // Dev Note: We need to expose the enum to allow the html template to use it in a condition.
    public get UWRequirementType() {
        return UWRequirementType;
    }

    public get CaseRequirementStatuses() {
        return CaseRequirementStatuses;
    }

    public get UWRequirementTableActions() {
        return UWRequirementTableActions;
    }

    //#endregion
    //#region Helpers

    private mapRequirementsToTableData() {
        const { integrationRequirements } = this.baseProps;
        this.uwRequirementsDataSet.data = UwwUtils.mapRequirementsToTableData(integrationRequirements, this.caseRequirements, this.requirements, this.notes, this.baseProps.caseIntegrationQueueEntries);  
    }

    private setNotOrderedTableProps(accountSettings: AccountSettingsDTO[]) {
        const uwDecisionSectionSetting = accountSettings?.find(x => x.name === AccountSettings.EvidenceNotRequiredForManualRequirements);

        if (uwDecisionSectionSetting && uwDecisionSectionSetting.value.toLowerCase() === "true") {
            this.tableProps.hideCompleteMenuOption = false;
        }else {
            this.tableProps.hideCompleteMenuOption = true;
        }
    }

    private setAutoReviewDocumentSetting(accountSettings: AccountSettingsDTO[]) {
        const autoReviewDocumentSetting = accountSettings?.find(x => x.name === AccountSettings.autoReviewDocumentSetting);
        
        if (autoReviewDocumentSetting && autoReviewDocumentSetting.value.toLowerCase() === "true") {
            this.tableProps.autoReviewDocumentSetting = true;
        }else {
            this.tableProps.autoReviewDocumentSetting = false;
        }
    }

    //#endregion
    //#region Subscriptions

    private subscribeToAccountSettings() {
        this.subs.add(this.caseSummarySettingsService.loadAccountSettings(this.baseProps.account.id).subscribe(accountSettings => {
            this.setNotOrderedTableProps(accountSettings);
            this.setAutoReviewDocumentSetting(accountSettings);
        }));
    }
    
    private subsribeToFeatureToggle() {
        const { client } = this.baseProps;

        forkJoin([            
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsFollowupDateAllowToday, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsTableDesign, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsReviewedClient, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchFeatureReviewDocumentsClients, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsHidePendingClient, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsHideCancelledClient, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsHideDeleteClient, client.id),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsHideEndStateToEndStateTransitionClient, client.id),
        ]).subscribe(([allowTodayEnabled,updatedTableDesignEnabled, showRequirementLabel, reviewDocumentsEnabled, hidePending, hideCancelled, hideDelete, hideEndStateToEndStateTransition]) => {                       
            this.tableProps.showRequirementLabel = showRequirementLabel;
            this.tableProps.reviewDocumentsEnabled = reviewDocumentsEnabled;
            this.tableProps.hideSetPendingMenuOption = hidePending;
            this.tableProps.hideSetCancelledMenuOption = hideCancelled;
            this.tableProps.hideDeleteMenuOption = hideDelete;
            this.tableProps.hideEndStateToEndStateTransition = hideEndStateToEndStateTransition;
            this.tableProps.updatedTableDesignEnabled = updatedTableDesignEnabled; // The new table design is tied to the updatedTableDesignEnabled feature toggle
            this.featureTogglesLoaded = true;

            if (allowTodayEnabled) {                
                this.minDate = moment.utc().startOf('day');
                this.maxDate = moment.utc().add(12, 'month').startOf('day');
            }else{                
                this.minDate = moment().add(1, 'day').startOf('day');
                this.maxDate = moment().add(12, 'month').startOf('day');
            }
        });

    }

    private subscribeToNotes() {
        this.subs.add(this.uwRequirementsDataService.loadNotes(this.baseProps.client.code, this.baseProps.case.id).subscribe({
            next: notes => {
                this.notes = notes;
                this.notesLoaded = true;
                this.mapRequirementsToTableData();
            },
        }));
    }


    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.subsribeToFeatureToggle();
        this.subscribeToAccountSettings();
        this.subscribeToNotes();
    }

    ngOnChanges(changes: SimpleChanges) {
        // BUG-39752 people adding case requirement notes would lose data
        // Gehred figured out that angular on change event will fire for any
        // data that is bound on the form (https://www.telerik.com/blogs/angular-basics-detecting-updates-onchanges-lifecyle-hook).
        // Turns out BaseProps fires every 30-60 seconds
        // the result was mapRequirementsToTableData would clear the data table and reset it,
        // which in turn would reset all the components on the page.
        // The fix you see here has a weakness and that is if a user is editing a case requirement note
        // and any of the Case Requirements change, add followupdate, state change, etc. etc. then 
        // the note would still be lost. A redesign of the UI would be required to fix this or a much
        // more sophisticated way to update the CaseRequirement table and the associated lists.
        if (changes.caseRequirements) {
            this.mapRequirementsToTableData();
        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    handleViewDocuments(columnAction: UWRequirementTableActions, selectedRequirement: UWRequirementTableData) {
        const viewAction = columnAction == UWRequirementTableActions.ViewDocumentIcon;
        if (viewAction && !selectedRequirement?.hasDocument)
            return; // We're faking disabled button via CSS so we can stop event propagation, to prevent expanding the column by clicking disabled button

        const actionEvent: UWRequirementActionEvent = { action: columnAction, data: selectedRequirement };
        this.onMenuAction.emit(actionEvent);
    }

    handleMenuAction(actionEvent: UWRequirementActionEvent) {
        this.onMenuAction.emit(actionEvent);
    }

    onFollowUpDateChange(caseRequirement: UWRequirementTableData, value: Moment | null) {
        if (value !== null && value !== undefined) {
            const date = moment.utc(value).startOf('day');
            if (!date.isValid() || date.isBefore(this.minDate) || date.isAfter(this.maxDate)) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Error,
                message: `Follow-up date must be between ${moment(this.minDate).format('M/D/YYYY')} and ${moment(this.maxDate).format('M/D/YYYY')}.`
            });
            return;
            }
        }

        const formattedDate = value ? moment.utc(value).format('YYYY-MM-DD') : null;
        const integrationRequirements = this.baseProps.integrationRequirements;
        const integration = integrationRequirements.find(x => x.integrationId == caseRequirement.integrationId && x.integrationActionId == caseRequirement.integrationActionId);
        if (integration) {
            this.subs.add(this.uwWorkbenchService.updateCaseRequirementFollowUpByIntegration(integration, formattedDate, this.baseProps.account.id, this.baseProps.case.id).subscribe({
                next: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: 'Requirement updated successfully.',
                        log: false
                    });
                }
            }));
        }        
        else if (caseRequirement.caseRequirementId) {
            this.subs.add(this.uwWorkbenchService.updateCaseRequirementFollowUp(caseRequirement.caseRequirementId, formattedDate, this.baseProps.account.id).subscribe({
                next: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: 'Requirement updated successfully.',
                        log: false
                    });
                }
            }));
        }
        else {
            console.error('Could not update Requirement because there was no case requirement id and it does not have an integration');
        }
    }

    //#endregion
}
